import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ShieldCheck, Zap, Map, Users, Sparkles, Apple, Play, Monitor, Globe, Check, ShoppingCart, CalendarDays, MessageCircle } from 'lucide-react';
import { TIERS } from '../data/mockData';

export default function Landing() {
  return (
    <div data-testid="page-landing">
      {/* ===== HERO ===== */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/17158868/pexels-photo-17158868.jpeg"
            alt="Custom sports cars at night"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0d0d0d] via-[#0d0d0d]/85 to-[#0d0d0d]/30" />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#0d0d0d]" />
        </div>
        <div className="am-grain absolute inset-0" />

        <div className="relative mx-auto max-w-7xl px-6 lg:px-10 pt-20 pb-28 lg:pt-32 lg:pb-40 grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8 am-rise">
            <div className="am-pill" data-testid="hero-pill">
              <span className="w-1.5 h-1.5 bg-[#ff2a2a] rounded-full am-dot" />
              LIVE · Cloudflare Edge · v1.0
            </div>
            <h1 className="mt-6 font-display font-bold text-5xl sm:text-6xl lg:text-7xl xl:text-8xl leading-[0.9] uppercase tracking-tighter" data-testid="hero-title">
              Your ride.<br/>
              Your rules.<br/>
              <span className="text-[#ff2a2a]">Your community.</span>
            </h1>
            <p className="mt-6 max-w-xl text-base sm:text-lg text-zinc-300 leading-relaxed" data-testid="hero-sub">
              Buy and sell vehicles with fair commissions. Share rides with people who actually know what a torque curve is. Attend meets. All in one app — free to download on every device.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/download" data-testid="hero-cta-download" className="group inline-flex items-center gap-2 bg-[#ff2a2a] hover:bg-[#e01f1f] text-white text-sm font-semibold px-6 py-3.5 rounded-sm transition-colors">
                Download the app <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </Link>
              <Link to="/marketplace" data-testid="hero-cta-marketplace" className="inline-flex items-center gap-2 hairline hover:border-white/30 text-white text-sm font-semibold px-6 py-3.5 rounded-sm transition-colors bg-white/[0.03]">
                Explore Marketplace
              </Link>
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-2 text-xs font-mono uppercase tracking-widest text-zinc-500">
              <span>Available on</span>
              <span className="text-white flex items-center gap-1"><Apple className="w-3.5 h-3.5"/>iOS</span>
              <span className="opacity-40">·</span>
              <span className="text-white flex items-center gap-1"><Play className="w-3.5 h-3.5"/>Android</span>
              <span className="opacity-40">·</span>
              <span className="text-white flex items-center gap-1"><Monitor className="w-3.5 h-3.5"/>Windows</span>
              <span className="opacity-40">·</span>
              <span className="text-white flex items-center gap-1"><Globe className="w-3.5 h-3.5"/>Web / PWA</span>
            </div>
          </div>

          <div className="lg:col-span-4 am-rise" style={{ animationDelay: '0.15s' }}>
            <div className="am-card p-6">
              <div className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-4">Get started in 3 steps</div>
              <ol className="space-y-4">
                {[
                  ['01', 'Download the app', 'iOS · Android · Windows · PWA'],
                  ['02', 'Browse or list a vehicle', 'Free inquiries on every listing'],
                  ['03', 'Book transport & drive', 'Coverage included at checkout'],
                ].map(([n, t, s]) => (
                  <li key={n} className="flex items-start gap-3">
                    <div className="font-mono text-xs text-[#ff2a2a] pt-0.5">{n}</div>
                    <div>
                      <div className="text-sm text-white font-semibold">{t}</div>
                      <div className="text-xs text-zinc-500 mt-0.5">{s}</div>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>
      </section>

      {/* ===== FEATURES STRIP ===== */}
      <section className="hairline-t hairline-b bg-[#0a0a0a]" data-testid="features-strip">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 grid grid-cols-2 md:grid-cols-4 divide-x divide-white/5">
          {[
            { icon: Zap, label: 'Commission-based sales', sub: 'No listing fees. Ever.' },
            { icon: Users, label: 'Trust-based rides', sub: 'Verified enthusiasts only.' },
            { icon: Map, label: 'Maps & travel', sub: 'Best routes. Best stops.' },
            { icon: ShieldCheck, label: 'PIPEDA privacy', sub: 'Your data stays yours.' },
          ].map((f, i) => (
            <div key={i} className="px-6 py-8 flex items-start gap-3">
              <f.icon className="w-5 h-5 text-[#ff2a2a] shrink-0 mt-0.5" />
              <div>
                <div className="text-sm text-white font-semibold">{f.label}</div>
                <div className="text-xs text-zinc-500 mt-1">{f.sub}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ===== HOW IT WORKS ===== */}
      <section className="mx-auto max-w-7xl px-6 lg:px-10 py-24" data-testid="how-it-works">
        <div className="max-w-2xl mb-12">
          <div className="font-mono text-xs uppercase tracking-widest text-[#ff2a2a] mb-2">01 · How it works</div>
          <h2 className="font-display text-4xl sm:text-5xl uppercase tracking-tight text-white leading-none">
            Buy, sell, ride, meet — one platform.
          </h2>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { icon: ShoppingCart, title: 'Marketplace', text: 'Browse verified listings. Contact sellers with free inquiries. Direct owner-to-buyer purchases.', to: '/marketplace' },
            { icon: MessageCircle, title: 'Free inquiry', text: 'Ask any question about any vehicle at zero cost. Arrange viewings, delivery, and pickup directly with the seller.', to: '/marketplace' },
            { icon: Users, title: 'Ride share', text: 'Verified enthusiasts only. Real driver profiles. Paid commute, shared fuel, shared conversations.', to: '/rides' },
            { icon: CalendarDays, title: 'Events', text: 'Meets, cars & coffee, track days, shows. RSVP with one tap. Host your own for free.', to: '/events' },
          ].map((c, i) => (
            <Link to={c.to} key={i} className="am-card p-6 flex flex-col group" data-testid={`how-card-${i}`}>
              <c.icon className="w-6 h-6 text-[#ff2a2a]" />
              <div className="mt-4 font-display text-xl uppercase tracking-tight">{c.title}</div>
              <div className="mt-2 text-sm text-zinc-400 flex-1">{c.text}</div>
              <div className="mt-4 text-xs font-mono uppercase tracking-widest text-zinc-500 group-hover:text-[#ff2a2a] transition-colors inline-flex items-center gap-1">
                Explore <ArrowRight className="w-3.5 h-3.5"/>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* ===== MAPS & AMENITIES CALLOUT ===== */}
      <section className="relative py-20" data-testid="maps-callout">
        <div className="absolute inset-0">
          <img src="https://images.pexels.com/photos/17785652/pexels-photo-17785652.jpeg" alt="Highway" className="w-full h-full object-cover opacity-25"/>
          <div className="absolute inset-0 bg-gradient-to-b from-[#0d0d0d] via-[#0d0d0d]/70 to-[#0d0d0d]" />
        </div>
        <div className="relative mx-auto max-w-7xl px-6 lg:px-10 grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-7">
            <div className="font-mono text-xs uppercase tracking-widest text-[#ff2a2a] mb-2">02 · Maps &amp; amenities</div>
            <h2 className="font-display text-4xl sm:text-5xl uppercase tracking-tight text-white leading-none">
              Every stop <span className="text-[#ff2a2a]">on the way there.</span>
            </h2>
            <p className="mt-4 text-zinc-300 max-w-lg">
              Find gas stations, EV chargers, service shops, food, and rest stops along your route. Every result opens directly in Google Maps for turn-by-turn directions.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/maps" className="bg-[#ff2a2a] hover:bg-[#e01f1f] text-white text-sm font-semibold px-5 py-3 transition-colors inline-flex items-center gap-2">
                Open the map <ArrowRight className="w-4 h-4"/>
              </Link>
              <Link to="/rides" className="hairline hover:border-white/30 text-white text-sm font-semibold px-5 py-3 transition-colors inline-flex items-center gap-2">
                Find a ride
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5 grid grid-cols-3 gap-3">
            {[
              ['Gas', 'Nearby stations'],
              ['EV', 'Charge fast'],
              ['Service', 'Trusted shops'],
            ].map(([n, c]) => (
              <div key={n} className="am-card p-4 text-center">
                <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{n}</div>
                <div className="mt-2 font-display text-xl text-white">Live</div>
                <div className="mt-1 text-[10px] font-mono uppercase tracking-widest text-zinc-500">{c}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== PRICING SNAPSHOT ===== */}
      <section className="mx-auto max-w-7xl px-6 lg:px-10 py-24" data-testid="pricing-preview">
        <div className="text-center max-w-2xl mx-auto">
          <div className="font-mono text-xs uppercase tracking-widest text-[#ff2a2a] mb-2">03 · Pricing</div>
          <h2 className="font-display text-4xl sm:text-5xl uppercase tracking-tight text-white leading-none">Fair for drivers. Fair for dealers.</h2>
          <p className="mt-4 text-zinc-400">Start free, upgrade when you&rsquo;re ready. Cancel anytime, no clauses.</p>
        </div>
        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {TIERS.map((t) => (
            <div
              key={t.id}
              className={`am-card p-6 relative ${t.highlight ? 'border-[#ff2a2a]' : ''}`}
              data-testid={`preview-tier-${t.id}`}
            >
              {t.highlight && (
                <div className="absolute -top-3 left-6 bg-[#ff2a2a] text-white text-[10px] font-mono uppercase tracking-widest px-2 py-1">
                  Most Popular
                </div>
              )}
              <div className="font-mono text-xs uppercase tracking-widest text-zinc-500">{t.name}</div>
              <div className="mt-2 flex items-baseline gap-1">
                <div className="font-display text-4xl text-white">${t.price}</div>
                <div className="text-xs text-zinc-500">{t.cadence}</div>
              </div>
              <div className="text-xs text-zinc-400 mt-1">{t.tagline}</div>
              <ul className="mt-5 space-y-2">
                {t.features.slice(0, 4).map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-zinc-300">
                    <Check className="w-4 h-4 text-[#ff2a2a] mt-0.5 shrink-0"/>{f}
                  </li>
                ))}
              </ul>
              <Link to="/pricing" className={`mt-6 block text-center py-2.5 text-sm font-semibold transition-colors ${t.highlight ? 'bg-[#ff2a2a] text-white hover:bg-[#e01f1f]' : 'hairline text-white hover:border-[#ff2a2a]'}`}>
                {t.cta}
              </Link>
            </div>
          ))}
        </div>
      </section>

      {/* ===== DOWNLOAD CTA ===== */}
      <section className="mx-auto max-w-7xl px-6 lg:px-10 pb-24" data-testid="cta-download">
        <div className="relative overflow-hidden hairline bg-gradient-to-br from-[#141414] to-[#0a0a0a] p-10 lg:p-16">
          <div className="am-grain absolute inset-0" />
          <div className="relative grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <Sparkles className="w-8 h-8 text-[#ff2a2a]" />
              <h2 className="mt-4 font-display text-4xl sm:text-5xl uppercase tracking-tight text-white leading-none">
                One app. Every device. Zero friction.
              </h2>
              <p className="mt-4 text-zinc-400 max-w-lg">
                Install AutoMobile on your iPhone, Android, Windows PC, or drop it right onto your vehicle&rsquo;s infotainment as a PWA.
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Link to="/download" className="inline-flex items-center gap-2 bg-white text-black text-sm font-semibold px-5 py-3 rounded-sm hover:bg-zinc-200 transition-colors" data-testid="cta-download-btn">
                <Apple className="w-4 h-4" /> iOS <span className="am-pill ml-2 !py-0.5 !px-2 !text-[9px]">Soon</span>
              </Link>
              <Link to="/download" className="inline-flex items-center gap-2 bg-white text-black text-sm font-semibold px-5 py-3 rounded-sm hover:bg-zinc-200 transition-colors">
                <Play className="w-4 h-4" /> Android <span className="am-pill ml-2 !py-0.5 !px-2 !text-[9px]">Soon</span>
              </Link>
              <Link to="/download" className="inline-flex items-center gap-2 bg-white text-black text-sm font-semibold px-5 py-3 rounded-sm hover:bg-zinc-200 transition-colors">
                <Monitor className="w-4 h-4" /> Windows <span className="am-pill ml-2 !py-0.5 !px-2 !text-[9px]">Beta</span>
              </Link>
              <Link to="/download" className="inline-flex items-center gap-2 bg-[#ff2a2a] text-white text-sm font-semibold px-5 py-3 rounded-sm hover:bg-[#e01f1f] transition-colors">
                <Globe className="w-4 h-4" /> Install PWA
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
